import { useState } from 'react';
import API from '../utils/API';

const EMPTY_ORG = { organization_name: '', group_type: '', street_address: '', suite_number: '', city: '', state: 'CA', zip_code: '', website: '', main_phone: '', lobby_filer_id: '' };
const EMPTY_DIVISION = { division_name: '', division_type: '', street_address: '', suite_number: '', city: '', state: '', zip_code: '', website: '', division_phone: '' };

export default function AdminCreateOrgModal({ initialMode = 'organization', presetOrg = null, organizations, groupTypes, onClose, onCreated }) {
  const [mode, setMode] = useState(initialMode);
  const [org, setOrg] = useState({ ...EMPTY_ORG });
  const [division, setDivision] = useState({ ...EMPTY_DIVISION });
  const [parent, setParent] = useState(presetOrg);
  const [parentSearch, setParentSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const setOrgField = field => e => setOrg(o => ({ ...o, [field]: e.target.value }));
  const setDivisionField = field => e => setDivision(d => ({ ...d, [field]: e.target.value }));

  const matches = parentSearch.trim().length < 2 ? [] : organizations
    .filter(o => o.organization_name.toLowerCase().includes(parentSearch.trim().toLowerCase()))
    .slice(0, 8);

  const canSubmit = mode === 'organization'
    ? ['organization_name', 'group_type', 'street_address', 'city', 'state', 'zip_code'].every(key => org[key].trim())
    : Boolean(parent && division.division_name.trim());

  const submit = async e => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = mode === 'organization'
        ? await API.adminCreateOrganization(org)
        : await API.adminCreateDivision({ ...division, organization_id: parent.organization_id });
      await onCreated(mode, res.data);
    } catch (err) {
      setError(err.response?.data?.msg || 'Could not save. Please try again.');
      setSubmitting(false);
    }
  };

  const address = (values, set) => (
    <>
      <div className="acp-grid-2">
        <div className="acp-field">
          <label className="acp-label">Street Address{mode === 'organization' && ' *'}</label>
          <input className="acp-input" value={values.street_address} onChange={set('street_address')} />
        </div>
        <div className="acp-field">
          <label className="acp-label">Suite</label>
          <input className="acp-input" value={values.suite_number} onChange={set('suite_number')} />
        </div>
      </div>
      <div className="acp-grid-3">
        <div className="acp-field">
          <label className="acp-label">City{mode === 'organization' && ' *'}</label>
          <input className="acp-input" value={values.city} onChange={set('city')} />
        </div>
        <div className="acp-field">
          <label className="acp-label">State{mode === 'organization' && ' *'}</label>
          <input className="acp-input" maxLength={2} value={values.state} onChange={e => set('state')({ target: { value: e.target.value.toUpperCase() } })} />
        </div>
        <div className="acp-field">
          <label className="acp-label">Zip{mode === 'organization' && ' *'}</label>
          <input className="acp-input" value={values.zip_code} onChange={set('zip_code')} />
        </div>
      </div>
    </>
  );

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <form className="admin-modal admin-create-org-modal" onClick={e => e.stopPropagation()} onSubmit={submit}>
        <div className="admin-modal-header">
          <span className="admin-modal-title">Create {mode === 'organization' ? 'Organization' : 'Division'}</span>
        </div>

        <div className="admin-modal-body admin-create-org-body">
          {!presetOrg && (
            <div className="awards-subtabs">
              <button type="button" className={`awards-subtab ${mode === 'organization' ? 'active' : ''}`} onClick={() => { setMode('organization'); setError(''); }}>
                <i className="bi bi-building me-1" />Organization
              </button>
              <button type="button" className={`awards-subtab ${mode === 'division' ? 'active' : ''}`} onClick={() => { setMode('division'); setError(''); }}>
                <i className="bi bi-diagram-3 me-1" />Division
              </button>
            </div>
          )}

          {mode === 'organization' ? (
            <>
              <div className="acp-field">
                <label className="acp-label">Organization Name *</label>
                <input className="acp-input" autoFocus value={org.organization_name} onChange={setOrgField('organization_name')} />
              </div>
              <div className="acp-field">
                <label className="acp-label">Group Type *</label>
                <select className="acp-select" value={org.group_type} onChange={setOrgField('group_type')}>
                  <option value="">Select a group type…</option>
                  {groupTypes.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              </div>
              {address(org, setOrgField)}
              <div className="acp-grid-2">
                <div className="acp-field">
                  <label className="acp-label">Website</label>
                  <input className="acp-input" value={org.website} onChange={setOrgField('website')} />
                </div>
                <div className="acp-field">
                  <label className="acp-label">Main Phone</label>
                  <input className="acp-input" placeholder="(916) 555-0100" value={org.main_phone} onChange={setOrgField('main_phone')} />
                </div>
              </div>
              <div className="acp-field">
                <label className="acp-label">Lobby Filer ID</label>
                <input className="acp-input" value={org.lobby_filer_id} onChange={setOrgField('lobby_filer_id')} />
              </div>
            </>
          ) : (
            <>
              <div className="acp-field">
                <label className="acp-label">Organization *</label>
                {parent ? (
                  <div className="admin-create-org-parent">
                    <span>{parent.organization_name}</span>
                    {!presetOrg && (
                      <button type="button" className="admin-create-org-change" onClick={() => { setParent(null); setParentSearch(''); }}>Change</button>
                    )}
                  </div>
                ) : (
                  <>
                    <input className="acp-input" autoFocus placeholder="Search organizations…" value={parentSearch} onChange={e => setParentSearch(e.target.value)} />
                    {matches.length > 0 && (
                      <div className="admin-create-org-results">
                        {matches.map(o => (
                          <button type="button" key={o.organization_id} onClick={() => setParent(o)}>
                            {o.organization_name}
                            {(o.city || o.state) && <span> · {[o.city, o.state].filter(Boolean).join(', ')}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="acp-grid-2">
                <div className="acp-field">
                  <label className="acp-label">Division Name *</label>
                  <input className="acp-input" autoFocus={Boolean(parent)} value={division.division_name} onChange={setDivisionField('division_name')} />
                </div>
                <div className="acp-field">
                  <label className="acp-label">Division Type</label>
                  <input className="acp-input" value={division.division_type} onChange={setDivisionField('division_type')} />
                </div>
              </div>
              {address(division, setDivisionField)}
              <div className="acp-grid-2">
                <div className="acp-field">
                  <label className="acp-label">Website</label>
                  <input className="acp-input" value={division.website} onChange={setDivisionField('website')} />
                </div>
                <div className="acp-field">
                  <label className="acp-label">Phone</label>
                  <input className="acp-input" placeholder="(916) 555-0100" value={division.division_phone} onChange={setDivisionField('division_phone')} />
                </div>
              </div>
            </>
          )}

          {error && <div className="admin-create-org-error">{error}</div>}
        </div>

        <div className="admin-create-org-footer">
          <button type="button" className="acp-btn-ghost" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="acp-btn-primary" disabled={!canSubmit || submitting}>
            {submitting ? 'Creating…' : `Create ${mode === 'organization' ? 'Organization' : 'Division'}`}
          </button>
        </div>
      </form>
    </div>
  );
}
